import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import type { MachineOption } from '../types';

interface UseOptionsReturn {
  options: MachineOption[];
  addOption: (name: string, categoryIds?: string[]) => Promise<MachineOption | null>;
}

export function useOptions(orgId: string | null): UseOptionsReturn {
  const [options, setOptions] = useState<MachineOption[]>([]);

  useEffect(() => {
    if (!orgId) {
      setOptions([]);
      return;
    }
    const load = async () => {
      try {
        const { data } = await supabase
          .from('options')
          .select('id, name, organization_id, archived, created_at')
          .eq('organization_id', orgId)
          .eq('archived', false)
          .order('name');
        setOptions(data ?? []);
      } catch {
        setOptions([]);
      }
    };
    load();
  }, [orgId]);

  const addOption = async (name: string, categoryIds: string[] = []): Promise<MachineOption | null> => {
    if (!orgId) return null;
    try {
      const { data, error } = await supabase
        .from('options')
        .insert({ name, organization_id: orgId, archived: false })
        .select()
        .single();
      if (error) throw error;
      const created = data as MachineOption;
      if (categoryIds.length > 0) {
        const { error: linkError } = await supabase.from('category_options').insert(
          categoryIds.map((category_id) => ({ category_id, option_id: created.id, organization_id: orgId }))
        );
        if (linkError) throw linkError;
      }
      const withLinks: MachineOption = { ...created, category_ids: categoryIds };
      setOptions((prev) => sortByName([...prev, withLinks]));
      return withLinks;
    } catch {
      return null;
    }
  };

  return { options, addOption };
}
